import type { QueryClient } from '@tanstack/react-query'
import type { LiveEvent, Workspace, WorkspaceMessage, WorkspaceMessageList } from '@one-deployment/api'
import { qk } from './query'

/** What the shell should surface for an event beyond the cache (a toast). */
export type EventEffect = { toast?: { level: 'down' | 'info'; text: string } }

/** One chunk of a running job's terminal output, appended as it arrives. */
export type OutputChunk = { jobId: string; seq: number; chunk: string; at: number }

/** The live output kept per workspace is capped: a tab left open for a day must not grow without bound. */
export const OUTPUT_CAP = 5000

const str = (v: unknown) => (typeof v === 'string' ? v : null)

/**
 * Live event → cache. Patches where the event carries the new state,
 * invalidates where it only says "this changed". Output chunks are appended,
 * never refetched: at one per chunk, a refetch each would be the dashboard
 * DDoSing its own Worker.
 */
export function applyLiveEvent(qc: QueryClient, e: LiveEvent): EventEffect {
  const [kind, id = ''] = e.topic.split(/:(.*)/s)
  const project = kind === 'project' ? id : null
  const workspaceId = kind === 'workspace' ? id : str(e.data.workspaceId)

  switch (e.event) {
    case 'workspace.stage': {
      const stage = str(e.data.stage)
      if (workspaceId && stage) patchWorkspace(qc, workspaceId, { stage } as Partial<Workspace>)
      if (project) void qc.invalidateQueries({ queryKey: qk.board(project) })
      void qc.invalidateQueries({ queryKey: qk.overview() })
      void qc.invalidateQueries({ queryKey: qk.home() })
      return {}
    }
    case 'job.phase':
    case 'job.updated': {
      if (workspaceId) {
        const phase = str(e.data.phase)
        if (phase) patchWorkspace(qc, workspaceId, (ws) => (ws.liveJob ? { liveJob: { ...ws.liveJob, phase } } : {}))
        invalidateWorkspace(qc, workspaceId)
        void qc.invalidateQueries({ queryKey: qk.timeline(workspaceId) })
      }
      void qc.invalidateQueries({ queryKey: qk.home() })
      return {}
    }
    case 'job.event': {
      if (!workspaceId) return {}
      const chunk: OutputChunk = { jobId: str(e.data.jobId) ?? '', seq: Number(e.data.seq ?? 0), chunk: str(e.data.chunk) ?? '', at: Date.now() }
      qc.setQueryData<OutputChunk[]>(qk.output(workspaceId), (old = []) => {
        if (old.some((c) => c.jobId === chunk.jobId && c.seq === chunk.seq)) return old
        const next = [...old, chunk]
        return next.length > OUTPUT_CAP ? next.slice(-OUTPUT_CAP) : next
      })
      return {}
    }
    case 'session.opened':
    case 'session.closed':
    case 'session.presence': {
      if (workspaceId) {
        void qc.invalidateQueries({ queryKey: qk.sessions(workspaceId) })
        invalidateWorkspace(qc, workspaceId)
      }
      void qc.invalidateQueries({ queryKey: qk.home() })
      return {}
    }
    case 'container.healed': {
      void qc.invalidateQueries({ queryKey: qk.home() })
      void qc.invalidateQueries({ queryKey: qk.containers() })
      return {}
    }
    case 'checks.updated': {
      if (workspaceId) void qc.invalidateQueries({ queryKey: qk.checks(workspaceId) })
      return {}
    }
    case 'alert.opened':
    case 'alert.cleared': {
      void qc.invalidateQueries({ queryKey: qk.alerts() })
      void qc.invalidateQueries({ queryKey: qk.overview() })
      const what = str(e.data.what)
      if (e.event === 'alert.opened' && e.data.level === 'down' && what) return { toast: { level: 'down', text: what } }
      return {}
    }
    case 'usage.updated': {
      void qc.invalidateQueries({ queryKey: qk.overview() })
      return {}
    }
    case 'message.created': {
      const m = e.data.message as WorkspaceMessage | undefined
      if (m) addMessage(qc, m)
      return {}
    }
    case 'message.delivered': {
      const mid = str(e.data.messageId)
      const at = typeof e.data.deliveredAt === 'number' ? e.data.deliveredAt : null
      if (workspaceId && mid && at) {
        qc.setQueryData<WorkspaceMessageList>(qk.messages(workspaceId), (old) =>
          old ? { ...old, messages: old.messages.map((m) => (m.id === mid ? { ...m, deliveredAt: at } : m)) } : old)
      }
      return {}
    }
    case 'server.updated': {
      void qc.invalidateQueries({ queryKey: qk.servers() })
      void qc.invalidateQueries({ queryKey: qk.overview() })
      return {}
    }
    case 'container.updated': {
      void qc.invalidateQueries({ queryKey: qk.containers() })
      return {}
    }
    case 'claude.accounts': {
      void qc.invalidateQueries({ queryKey: qk.claudeAccounts() })
      void qc.invalidateQueries({ queryKey: ['usage', 'summary'] })
      void qc.invalidateQueries({ queryKey: qk.overview() })
      return {}
    }
    case 'claude.login': {
      // Only the id travels on the hub; the sign-in URL is fetched by whoever started it.
      const id = str(e.data.loginId)
      if (id) void qc.invalidateQueries({ queryKey: qk.claudeLogin(id) })
      void qc.invalidateQueries({ queryKey: qk.claudeAccounts() })
      return {}
    }
    case 'session.account_switched': {
      if (workspaceId) {
        void qc.invalidateQueries({ queryKey: qk.sessions(workspaceId) })
        void qc.invalidateQueries({ queryKey: qk.timeline(workspaceId) })
      }
      void qc.invalidateQueries({ queryKey: qk.claudeAccounts() })
      const to = str(e.data.toLabel), from = str(e.data.fromLabel)
      return to ? { toast: { level: 'info', text: `Claude moved ${from ? `from ${from} ` : ''}to ${to} (${e.data.reason === 'auth' ? 'login refused' : 'usage limit'})` } } : {}
    }
    case 'inbox.changed': {
      void qc.invalidateQueries({ queryKey: qk.home() })
      void qc.invalidateQueries({ queryKey: qk.overview() })
      return {}
    }
    case 'activity.changed': {
      void qc.invalidateQueries({ queryKey: qk.home() })
      void qc.invalidateQueries({ queryKey: qk.overview() })
      return {}
    }
    case 'project.updated': {
      // A reconnect on the global topic: anything it carries may have been missed.
      if (kind === 'global') for (const k of [qk.alerts(), qk.servers(), qk.containers(), qk.home(), qk.claudeAccounts()]) void qc.invalidateQueries({ queryKey: k })
      if (project) void qc.invalidateQueries({ queryKey: qk.project(project) })
      if (workspaceId) void qc.invalidateQueries({ queryKey: qk.wsAll(workspaceId) })
      if (workspaceId) invalidateWorkspace(qc, workspaceId)
      void qc.invalidateQueries({ queryKey: qk.projects() })
      void qc.invalidateQueries({ queryKey: qk.overview() })
      return {}
    }
    case 'ci.updated': {
      if (project) void qc.invalidateQueries({ queryKey: qk.ci(project) })
      return {}
    }
    case 'ci.log': {
      // A chunk per line batch: the open log polls every few seconds instead of refetching on each.
      return {}
    }
  }
}

/**
 * Add a stored chat message to its workspace's list, newest first, once: the
 * sender's own POST response and the hub's echo both arrive.
 */
export function addMessage(qc: QueryClient, m: WorkspaceMessage) {
  qc.setQueryData<WorkspaceMessageList>(qk.messages(m.workspaceId), (old) => {
    if (!old) return old
    const at = old.messages.findIndex((x) => x.id === m.id)
    if (at >= 0) {
      const merged = { ...m, deliveredAt: old.messages[at]!.deliveredAt ?? m.deliveredAt }
      return { ...old, messages: old.messages.map((x, i) => (i === at ? merged : x)) }
    }
    return { ...old, messages: [m, ...old.messages] }
  })
}

const isWorkspaceKey = (id: string) => (q: { queryKey: readonly unknown[]; state: { data: unknown } }) =>
  q.queryKey[0] === 'workspace' && (q.state.data as Workspace | undefined)?.id === id

/** Workspaces are cached by ticket (the URL), but events name the id: find it by its data. */
function patchWorkspace(qc: QueryClient, id: string, patch: Partial<Workspace> | ((ws: Workspace) => Partial<Workspace>)) {
  for (const q of qc.getQueryCache().findAll({ predicate: isWorkspaceKey(id) })) {
    qc.setQueryData<Workspace>(q.queryKey, (old) => (old ? { ...old, ...(typeof patch === 'function' ? patch(old) : patch) } : old))
  }
}

function invalidateWorkspace(qc: QueryClient, id: string) {
  void qc.invalidateQueries({ predicate: isWorkspaceKey(id) })
}
